import { useQuery } from '@tanstack/react-query';
import { get } from '@/shared/api/api-client';

/**
 * GET /api/Organization/{organizationId}
 *
 * Returns org branding info. This is the authoritative source for the
 * patient-sticker logo: Flutter dispatches `FetchOrganisationDetailEvent`
 * when Print is clicked and sets `brandingUrl = organisationInfo.brandingUrl`
 * (`patient_list_screen.dart:550`) — NOT the login user's brandingUrl.
 *
 * Wire shape (Flutter `OrganisationInfo.fromJson`,
 * `permissions/.../organisation_info.dart`): note `photoURL` is PascalCase-ish
 * in the JSON, `brandingUrl` / `name` are camelCase.
 *   { "photoURL": string?, "brandingUrl": string?, "name": string? }
 */
export interface OrganisationInfo {
  photoUrl: string | null;
  brandingUrl: string | null;
  name: string | null;
}

interface OrganisationInfoWire {
  photoURL?: string | null;
  brandingUrl?: string | null;
  name?: string | null;
}

export function useOrganisationInfo(
  organizationId: string | null | undefined,
) {
  return useQuery({
    queryKey: ['organisation-info', organizationId],
    queryFn: async ({ signal }) => {
      const res = await get<OrganisationInfoWire>(
        `/api/Organization/${organizationId}`,
        { signal },
      );
      return {
        photoUrl: res?.photoURL ?? null,
        brandingUrl: res?.brandingUrl ?? null,
        name: res?.name ?? null,
      } satisfies OrganisationInfo;
    },
    enabled: !!organizationId,
    staleTime: 1000 * 60 * 30, // branding rarely changes
  });
}
